import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { type IAuthUser } from './useAuthStore'
import { useBrowserStore } from './useBrowserStore'


export const useGlobalStore = defineStore('global', () => {
  const browserStore = useBrowserStore()

  const role = ref<number>(-1)
  const userId = ref<number>(-1)

  const accessToken = useCookie<string | null>('auth_token', {
    maxAge: 60 * 60, // 1 hour
    sameSite: 'lax',
  })

  const refreshToken = useCookie<string | null>('refresh_token', {
    maxAge: 60 * 60 * 24, // 1 day
  })

  const isAuthenticated = computed(() => !!accessToken.value)
  const getToken = computed(() => accessToken.value || '')
  const getRefreshToken = computed(() => refreshToken.value || '')
  const getUserData = computed(() => browserStore.getFromCookie<IAuthUser>("user"))

  const isLocalhostOrStage = computed(() => {
    if (!import.meta.client) return false
    return window.location.href.includes("localhost") || window.location.href.includes("stage")
  })

  // Snackbar state
  const snackbar = ref({
    show: false,
    message: '',
    color: 'success',
    timeout: 3000
  })

  function showSnackbar(message: string, color: 'success' | 'error' | 'warning' | 'info' = 'success', timeout = 3000) {
    snackbar.value = { show: true, message, color, timeout }
  }

  function updateUser(userData: IAuthUser) {
    browserStore.addToCookie("user", userData)
  }

  function updateRole(newRole: number) {
    role.value = newRole
    browserStore.addToCookie("role", newRole.toString())
  }

  function updateToken(newAccessToken: string, newRefreshToken: string = "") {
    accessToken.value = newAccessToken
    browserStore.addToCookie("token", newAccessToken)
    
    if (newRefreshToken) {
      refreshToken.value = newRefreshToken
      browserStore.addToCookie("refreshToken", newRefreshToken)
    }
  }

  function updateRefreshToken(newRefreshToken: string) {
    refreshToken.value = newRefreshToken
    browserStore.addToCookie("refreshToken", newRefreshToken)
  }

  function clearToken() {
    accessToken.value = ""
    browserStore.removeFromSession("token")
  }

  function clearAll(resetSession = true) {
    accessToken.value = ""
    refreshToken.value = ""
    role.value = -1

    if (resetSession) {
      browserStore.clearAllCookies()
    }
  }

  return {
    // State
    role,
    userId,
    accessToken,
    refreshToken,
    getUserData,
    snackbar,
    // Getters
    isAuthenticated,
    getToken,
    getRefreshToken,
    isLocalhostOrStage,
    // Actions
    updateUser,
    updateRole,
    updateToken,
    updateRefreshToken,
    clearToken,
    clearAll,
    showSnackbar
  }
})